import { type TabData } from "@utils/dashboard-storage";
import { type TabBulkUpdateItem } from "@models/Dashboards";
import { trim, isTemp } from "./utils";

export const GROUP_NAME_MAX_LENGTH = 40;

export type TabNavItem =
  | { kind: "tab"; tab: TabData }
  | { kind: "group"; groupName: string; tabs: TabData[] };

export const buildNavItems = (tabs: TabData[]): TabNavItem[] => {
  const items: TabNavItem[] = [];
  for (const tab of tabs) {
    const last = items[items.length - 1];
    if (tab.groupName && last?.kind === "group" && last.groupName === tab.groupName) {
      last.tabs.push(tab);
    } else if (tab.groupName) {
      items.push({ kind: "group", groupName: tab.groupName, tabs: [tab] });
    } else {
      items.push({ kind: "tab", tab });
    }
  }
  return items;
};

export const getGroupNames = (tabs: TabData[]): string[] =>
  Array.from(new Set(tabs.map((t) => t.groupName).filter((g): g is string => !!g)));

export const getGroupMembers = (tabs: TabData[], groupName: string): TabData[] =>
  tabs.filter((t) => t.groupName === groupName);

/**
 * A group left with one member no longer meets the "valid group has >=2 tabs"
 * rule, so it gets ungrouped in place. Centralized here so every mutating
 * operation below can just call this instead of re-deriving the rule.
 */
export const dissolveSingleMemberGroups = (tabs: TabData[]): TabData[] => {
  const counts = new Map<string, number>();
  tabs.forEach((t) => {
    if (t.groupName) counts.set(t.groupName, (counts.get(t.groupName) ?? 0) + 1);
  });
  const toDissolve = new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count < 2)
      .map(([name]) => name),
  );
  if (toDissolve.size === 0) return tabs;
  return tabs.map((t) => (t.groupName && toDissolve.has(t.groupName) ? { ...t, groupName: null } : t));
};

export const computeBulkUpdatePayload = (tabs: TabData[]): TabBulkUpdateItem[] =>
  tabs
    .filter((t) => !isTemp(t.id))
    .map((t, index) => ({
      tabId: t.id,
      groupName: t.groupName,
      tabOrderIndex: index + 1,
    }));

/** True when two tab lists have the exact same order and group assignments. */
export const isSameTabOrder = (a: TabData[], b: TabData[]): boolean =>
  a.length === b.length && a.every((t, i) => t.id === b[i].id && t.groupName === b[i].groupName);

export const validateGroupName = (
  name: string,
  tabs: TabData[],
  excludeGroupName?: string,
): string | null => {
  const trimmed = trim(name);
  if (trimmed.length < 1) return "Group name must contain at least 1 character";
  if (trimmed.length > GROUP_NAME_MAX_LENGTH)
    return `Group name must be ${GROUP_NAME_MAX_LENGTH} characters or fewer`;

  const lower = trimmed.toLowerCase();
  const otherGroupNames = getGroupNames(tabs).filter(
    (g) => g.toLowerCase() !== excludeGroupName?.toLowerCase(),
  );
  const tabLabels = tabs.map((t) => t.label);
  const taken = new Set([...otherGroupNames, ...tabLabels].map((n) => n.toLowerCase()));

  if (taken.has(lower))
    return "A group or tab with this name already exists. Use a unique group name.";
  return null;
};

/**
 * Creates a new group from the given tab ids. The group is inserted at the
 * position of the earliest-selected tab, preserving the selected tabs'
 * relative order; any source groups that lose members are re-checked for
 * dissolution.
 */
export const createGroup = (
  tabs: TabData[],
  selectedTabIds: string[],
  groupName: string,
): TabData[] => {
  const selectedSet = new Set(selectedTabIds);
  const earliestIndex = tabs.findIndex((t) => selectedSet.has(t.id));
  if (earliestIndex === -1) return tabs;

  const selectedInOrder = tabs.filter((t) => selectedSet.has(t.id));
  const remaining = tabs.filter((t) => !selectedSet.has(t.id));

  const insertAt = tabs
    .slice(0, earliestIndex)
    .filter((t) => !selectedSet.has(t.id)).length;

  const grouped = selectedInOrder.map((t) => ({ ...t, groupName: trim(groupName) }));
  const next = [...remaining.slice(0, insertAt), ...grouped, ...remaining.slice(insertAt)];

  return dissolveSingleMemberGroups(next);
};

export const renameGroup = (tabs: TabData[], oldName: string, newName: string): TabData[] =>
  tabs.map((t) => (t.groupName === oldName ? { ...t, groupName: trim(newName) } : t));

/** Ungroups every member in place; members keep their position and order. */
export const deleteGroup = (tabs: TabData[], groupName: string): TabData[] =>
  tabs.map((t) => (t.groupName === groupName ? { ...t, groupName: null } : t));

/**
 * Moves a single tab to a new group (or to ungrouped when targetGroupName is
 * null), inserting it immediately before insertBeforeTabId, or at the end of
 * the target group/dashboard when omitted.
 */
export const moveTabToGroup = (
  tabs: TabData[],
  tabId: string,
  targetGroupName: string | null,
  insertBeforeTabId: string | null,
): TabData[] => {
  // Dropping a tab onto itself is a no-op. Without this guard, insertBeforeTabId
  // would resolve to -1 below (it was just filtered out of `without`) and fall
  // through to the "append at end" branch, teleporting the tab to the end.
  if (insertBeforeTabId === tabId) return tabs;

  const moving = tabs.find((t) => t.id === tabId);
  if (!moving) return tabs;

  const without = tabs.filter((t) => t.id !== tabId);
  const moved: TabData = { ...moving, groupName: targetGroupName };

  let insertAt = insertBeforeTabId ? without.findIndex((t) => t.id === insertBeforeTabId) : -1;
  if (insertAt === -1 && targetGroupName) {
    // No explicit slot given: land at the end of the target group's block
    // rather than the end of the whole list, so the group stays contiguous.
    const lastMemberIndex = without.reduce(
      (acc, t, i) => (t.groupName === targetGroupName ? i : acc),
      -1,
    );
    insertAt = lastMemberIndex === -1 ? -1 : lastMemberIndex + 1;
  }

  const next =
    insertAt === -1
      ? [...without, moved]
      : [...without.slice(0, insertAt), moved, ...without.slice(insertAt)];

  return dissolveSingleMemberGroups(next);
};

/** Reorders top-level nav items (ungrouped tabs and whole groups act as one movable block). */
export const reorderNavItem = (
  tabs: TabData[],
  fromNavIndex: number,
  toNavIndex: number,
): TabData[] => {
  const items = buildNavItems(tabs);
  if (fromNavIndex === toNavIndex || fromNavIndex < 0 || fromNavIndex >= items.length) return tabs;

  const copy = [...items];
  const [moved] = copy.splice(fromNavIndex, 1);
  copy.splice(toNavIndex, 0, moved);

  return copy.flatMap((item) => (item.kind === "tab" ? [item.tab] : item.tabs));
};

export const reorderWithinGroup = (
  tabs: TabData[],
  groupName: string,
  from: number,
  to: number,
): TabData[] => {
  const members = getGroupMembers(tabs, groupName);
  if (from === to || from < 0 || from >= members.length) return tabs;

  const reordered = [...members];
  const [moved] = reordered.splice(from, 1);
  reordered.splice(to, 0, moved);

  let cursor = 0;
  return tabs.map((t) => (t.groupName === groupName ? reordered[cursor++] : t));
};
